/**
 * useQuoteDraftAutosave — Hook centralisé de gestion de l'autosave brouillon devis.
 *
 * Phase 3h — Bugs 9 & 10 (état non sync + validation stale).
 *
 * Comportement :
 *  1. Détecte les changements du brouillon via sérialisation JSON.
 *  2. Debounce 2 s après la dernière modification → PATCH auto.
 *  3. `saveNow()` : force un save immédiat (bypass debounce), retourne une Promise
 *     awaitable — utilisée par les actions (send / preview / export / duplicate)
 *     pour garantir que l'API travaille sur l'état frais.
 *  4. Intercepte la navigation React Router (`useBlocker`) pour save avant sortie.
 *  5. Écoute `beforeunload` pour prévenir la fermeture d'onglet avec modifs pending.
 *  6. Expose `{ status, lastSavedAt, saveNow, isDirty }` pour l'indicateur UI.
 *
 * Le hook NE modifie PAS le state du brouillon (`quote`) — c'est le parent qui gère
 * `setQuote`. Le hook lit `quote` en input et déclenche les PATCH côté serveur.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

export default function useQuoteDraftAutosave({
  quoteId,
  quote,
  sectionsOrder,
  showChantierAddr,
  isDraft,
  onSaved,
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSerializedRef = useRef(null);
  const inFlightRef = useRef(null);
  const timerRef = useRef(null);
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const buildPayload = useCallback(() => {
    if (!quote) return null;
    return {
      title: quote.title || "",
      lines: quote.lines,
      sections_order: sectionsOrder,
      notes: quote.notes || "",
      validity_days: quote.validity_days,
      chantier_address: showChantierAddr
        ? quote.chantier_address || { street: "", zip: "", city: "" }
        : { street: "", zip: "", city: "" },
      acompte_config_override: quote.acompte_config_override,
      payment_schedule_template_id: quote.payment_schedule_template_id || "",
      // Phase Rentab-3 (13/09/2026) — Estimation interne (admin only côté backend).
      // Le backend rejette 403 si un non-admin envoie ces champs — l'UI ne les
      // affiche qu'aux admins, mais on les inclut dans l'autosave uniquement
      // s'ils sont explicitement renseignés (évite les 403 sur payload zombie).
      ...(quote.labor_days_estimated !== undefined && quote.labor_days_estimated !== null
        ? { labor_days_estimated: quote.labor_days_estimated }
        : {}),
      ...(quote.subcontracting_estimated_htva !== undefined && quote.subcontracting_estimated_htva !== null
        ? { subcontracting_estimated_htva: quote.subcontracting_estimated_htva }
        : {}),
    };
  }, [quote, sectionsOrder, showChantierAddr]);

  const serialized = useMemo(() => {
    const p = buildPayload();
    return p ? JSON.stringify(p) : null;
  }, [buildPayload]);

  const isDirty = serialized !== null && serialized !== lastSerializedRef.current;

  // `saveNow` — API call idempotent (skip si aucun changement depuis dernier save réussi)
  const saveNow = useCallback(async () => {
    if (!isDraft || !quote || !serialized) return null;
    // Attend la requête en vol pour ne pas doublonner
    if (inFlightRef.current) {
      try {
        await inFlightRef.current;
      } catch (prevErr) {
        console.warn("[QuoteAutosave] Previous in-flight save failed (waiting for it):", prevErr?.message || prevErr);
      }
    }
    if (serialized === lastSerializedRef.current) {
      return null; // rien à sauver
    }
    setStatus("saving");
    const payload = JSON.parse(serialized);
    const promise = api
      .patch(`/quotes/${quoteId}`, payload)
      .then(({ data }) => {
        lastSerializedRef.current = serialized;
        setLastSavedAt(new Date());
        setStatus("saved");
        onSavedRef.current?.(data);
        return data;
      })
      .catch((e) => {
        setStatus("error");
        throw e;
      })
      .finally(() => {
        inFlightRef.current = null;
      });
    inFlightRef.current = promise;
    return promise;
  }, [quoteId, quote, isDraft, serialized]);

  // Baseline à la première hydratation : le state initial du serveur = référence
  useEffect(() => {
    if (lastSerializedRef.current === null && serialized !== null) {
      lastSerializedRef.current = serialized;
      setStatus("saved");
      setLastSavedAt(new Date());
    }
  }, [serialized]);

  // Debounce 2 s : dès qu'une modif apparaît, planifie un save
  useEffect(() => {
    if (!isDraft || !isDirty) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveNow().catch(() => {
        /* status="error" déjà positionné ; toast géré côté actions manuelles */
      });
    }, 2000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isDraft, isDirty, saveNow]);

  // beforeunload : prévient la fermeture d'onglet avec modifs pending
  useEffect(() => {
    const handler = (e) => {
      if (!isDraft || !isDirty) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDraft, isDirty]);

  // Intercepte les clics sur les liens sortants — flush debounce + saveNow avant navigation.
  // Alternative à useBlocker (indispo hors data router).
  useEffect(() => {
    if (!isDraft) return undefined;
    const clickHandler = (e) => {
      if (!isDirty) return;
      // Cherche un <a href> ancêtre du target (SPA React Router links)
      const anchor = e.target?.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || anchor.target === "_blank") return;
      // Ignore les liens vers l'URL courante
      if (href === window.location.pathname) return;
      // Intercepte : on empêche la nav, on save, puis on navigue via React Router
      e.preventDefault();
      e.stopPropagation();
      const proceed = () => navigate(href);
      saveNow().then(proceed).catch(proceed);
    };
    document.addEventListener("click", clickHandler, true);
    return () => document.removeEventListener("click", clickHandler, true);
  }, [isDraft, isDirty, saveNow, navigate]);

  return { status, lastSavedAt, saveNow, isDirty };
}